import React from "react";
import { Coins, Wallet, Clock, PiggyBank, Flame, ShieldCheck, TrendingDown } from "lucide-react";
import { useTranslation, useFormat } from "../../../i18n";
import { useAnalyticsSection } from "../../analytics/hooks";
import { KpiTile, ChartCard, Skeleton, ErrorState, DataTable, StatRow, ShareBar, RankList, PersonCell, EmptyState } from "../../analytics/primitives";
import { TrendChart, BarList, StackedTrendChart } from "../../analytics/charts";
import { SERIES, STATUS } from "../../analytics/palette";

const utilColor = (u) => (u === null || u === undefined ? STATUS.info : u >= 90 ? STATUS.critical : u >= 70 ? STATUS.warning : STATUS.good);

const PointsTab = ({ filters }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const q = useAnalyticsSection("points", filters);
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) return <Skeleton height={400} />;
  const d = q.data;
  const granularity = d.scope?.granularity;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));
  const hoursFmt = (h) => (h === null || h === undefined ? "—" : h < 48 ? t("hours_short", { count: Math.round(h) }) : t("days_short", { count: Math.round(h / 24) }));
  const money = (v) => fmt.currency(v || 0, d.budgets.currency || "USD");
  const budgetNames = Object.fromEntries(d.budgets.budgets.map((b) => [b.id, b.name]));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile icon={Coins} label={t("points.earned")} value={fmt.number(d.flow.earned.value)} metric={d.flow.earned} tone="green" />
        <KpiTile icon={TrendingDown} label={t("points.redeemed")} value={fmt.number(d.flow.redeemed.value)} metric={d.flow.redeemed} tone="pink" sub={t("points.redemption_rate", { rate: pctFmt(d.flow.redemption_rate) })} />
        <KpiTile icon={Wallet} label={t("points.liability")} value={fmt.number(d.liability.total_points)} tone="slate" sub={t("points.liability_sub", { value: fmt.currency(d.liability.total_value || 0, "USD"), count: d.liability.wallets_with_balance })} hint={t("kpi.liability_hint")} />
        <KpiTile icon={Clock} label={t("points.pending")} value={fmt.number(d.approvals.pending.count)} tone={d.approvals.pending.older_than_7d ? "red" : d.approvals.pending.count ? "yellow" : "slate"} sub={d.approvals.pending.count ? t("points.pending_sub", { points: fmt.number(d.approvals.pending.points), stale: d.approvals.pending.older_than_7d }) : t("points.queue_clear")} />
        <KpiTile icon={ShieldCheck} label={t("points.approval_rate")} value={pctFmt(d.approvals.approval_rate)} tone="blue" sub={t("points.decided", { approved: d.approvals.approved, rejected: d.approvals.rejected })} />
        <KpiTile icon={Clock} label={t("points.time_to_decision")} value={hoursFmt(d.approvals.median_hours_to_decision)} tone="teal" sub={t("points.within_24h", { rate: pctFmt(d.approvals.decided_within_24h_share) })} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard
          title={t("points.flow_title")}
          subtitle={t("points.flow_subtitle")}
          icon={Coins}
          className="lg:col-span-2"
          table={{
            columns: [
              { key: "bucket", label: t("table.period") },
              { key: "earned", label: t("points.earned"), align: "right" },
              { key: "redeemed", label: t("points.redeemed"), align: "right" },
              { key: "adjusted", label: t("points.adjusted"), align: "right" },
              { key: "net", label: t("points.net"), align: "right" },
            ],
            rows: d.flow.series,
            keyField: "bucket",
          }}
        >
          <TrendChart
            rows={d.flow.series}
            granularity={granularity}
            series={[
              { key: "earned", label: t("points.earned"), color: SERIES[2], kind: "bar" },
              { key: "redeemed", label: t("points.redeemed"), color: SERIES[1], kind: "bar" },
              { key: "net", label: t("points.net"), color: SERIES[0], kind: "line" },
            ]}
          />
        </ChartCard>
        <ChartCard title={t("points.sources_title")} icon={PiggyBank}>
          <BarList data={d.flow.by_source.map((s) => ({ ...s, name: `${t(`source.${s.source}`, { defaultValue: s.source })} · ${t(`tx_type.${s.type}`, { defaultValue: s.type })}` }))} valueKey="points" />
        </ChartCard>
      </div>

      <ChartCard title={t("points.budgets_title")} subtitle={t("points.budgets_subtitle")} icon={PiggyBank}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <StatRow label={t("points.allocated")} value={money(d.budgets.totals.allocated)} />
          <StatRow label={t("points.used")} value={money(d.budgets.totals.used)} />
          <StatRow label={t("points.reserved")} value={money(d.budgets.totals.reserved)} />
          <StatRow label={t("points.available")} value={money(d.budgets.totals.available)} />
          <StatRow label={t("points.spend_in_period")} value={money(d.budgets.totals.spend_in_period)} />
        </div>
        <DataTable
          keyField="id"
          rows={d.budgets.budgets}
          emptyMessage={t("points.no_budgets")}
          columns={[
            { key: "name", label: t("table.budget"), render: (b) => (
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{b.name}</p>
                <p className="text-[11px] text-slate-500 truncate">{b.team ? b.team.name : t("points.org_budget")}{b.owner ? ` · ${b.owner.full_name || b.owner.email}` : ""}</p>
              </div>
            ) },
            { key: "allocated", label: t("points.allocated"), align: "right", render: (b) => money(b.allocated) },
            { key: "utilisation", label: t("points.utilisation"), render: (b) => <ShareBar value={b.utilisation || 0} color={utilColor(b.utilisation)} label={pctFmt(b.utilisation)} /> },
            { key: "used", label: t("points.used"), align: "right", render: (b) => money(b.used) },
            { key: "reserved", label: t("points.reserved"), align: "right", render: (b) => money(b.reserved) },
            { key: "available", label: t("points.available"), align: "right", render: (b) => money(b.available) },
            { key: "spend_in_period", label: t("points.spend_in_period"), align: "right", render: (b) => (
              <span>
                {money(b.spend_in_period)}
                {b.spend_previous_period ? <span className="block text-[10px] text-slate-400">{t("points.prev_period", { value: money(b.spend_previous_period) })}</span> : null}
              </span>
            ) },
            { key: "daily_burn", label: t("points.daily_burn"), align: "right", render: (b) => (b.daily_burn === null ? "—" : money(b.daily_burn)) },
            { key: "runway_days", label: t("points.runway"), align: "right", sortValue: (b) => (b.runway_days === null ? Number.MAX_SAFE_INTEGER : b.runway_days), render: (b) =>
              b.runway_days === null ? (
                <span className="text-slate-400">—</span>
              ) : (
                <span className={`font-medium ${b.runway_days <= 30 ? "text-rose-600" : b.runway_days <= 90 ? "text-amber-600" : "text-slate-700"}`} title={b.projected_exhaustion ? t("points.exhausts_on", { date: fmt.date(new Date(`${b.projected_exhaustion}T00:00:00`)) }) : undefined}>
                  <Flame className="inline w-3 h-3 mr-0.5 -mt-0.5" aria-hidden="true" />
                  {t("days_short", { count: b.runway_days })}
                </span>
              ) },
          ]}
        />
        {d.budgets.spend_series.budget_ids.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-600 mb-1">{t("points.spend_over_time")}</p>
            <StackedTrendChart rows={d.budgets.spend_series.rows} keys={d.budgets.spend_series.budget_ids.map((id) => ({ key: id, name: budgetNames[id] || id }))} granularity={granularity} height={220} />
          </div>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("points.approvals_title")} subtitle={t("points.approvals_subtitle")} icon={ShieldCheck}>
          <StatRow label={t("points.requests_created")} value={fmt.number(d.approvals.created)} />
          <StatRow label={t("points.approved")} value={`${fmt.number(d.approvals.approved)} · ${fmt.number(d.approvals.approved_points)} ${t("points.pts")}`} />
          <StatRow label={t("points.rejected")} value={`${fmt.number(d.approvals.rejected)} · ${fmt.number(d.approvals.rejected_points)} ${t("points.pts")}`} />
          <StatRow label={t("points.avg_time_to_decision")} value={hoursFmt(d.approvals.avg_hours_to_decision)} />
          <StatRow label={t("points.within_72h_label")} value={pctFmt(d.approvals.decided_within_72h_share)} />
          <StatRow label={t("points.oldest_pending")} value={hoursFmt(d.approvals.pending.oldest_age_hours)} />
          {d.approvals.pending.oldest && (
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 p-2.5">
              <p className="text-[11px] font-semibold text-amber-800 mb-1">{t("points.oldest_pending_detail")}</p>
              <div className="flex items-center gap-2 text-xs">
                <PersonCell user={d.approvals.pending.oldest.sender} />
                <span className="text-slate-400">→</span>
                <PersonCell user={d.approvals.pending.oldest.recipient} />
                <span className="ml-auto font-semibold tabular-nums">{fmt.number(d.approvals.pending.oldest.amount)}</span>
              </div>
            </div>
          )}
        </ChartCard>
        <ChartCard title={t("points.top_balances")} subtitle={t("points.top_balances_subtitle")} icon={Wallet}>
          {d.liability.top_balances.length === 0 ? (
            <EmptyState height={140} />
          ) : (
            <RankList rows={d.liability.top_balances.map((b, i) => ({ ...b, rank: i + 1, value: b.balance }))} valueLabel={t("points.pts")} renderExtra={(r) => fmt.currency(r.value_usd ?? r.value * (d.liability.points_to_dollar_rate || 1), "USD")} />
          )}
          <div className="mt-2 grid grid-cols-3 gap-2">
            <StatRow label={t("points.avg_balance")} value={d.liability.avg_balance === null ? "—" : fmt.number(d.liability.avg_balance, { maximumFractionDigits: 0 })} />
            <StatRow label={t("points.median_balance")} value={d.liability.median_balance === null ? "—" : fmt.number(d.liability.median_balance, { maximumFractionDigits: 0 })} />
            <StatRow label={t("points.max_balance")} value={fmt.number(d.liability.max_balance)} />
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default PointsTab;
